import { runAfterGuideTourLayoutFrames } from './animationFrames'
import { waitForGuideTourPageScrollUnlock } from './scrollLock'
import { isGuideTourTargetInsideActiveModal } from '../routing/activation'
import {
  isGuideTourTargetVisibleInViewport,
  waitForGuideTourScrollSettle,
  waitForGuideTourTargetPositionStability,
} from './targetVisibility'
import {
  findGuideTourTargetElement,
  getGuideTourTargetBounds,
  type SpotlightBounds,
} from './utils'

export const ignoreRejectedGuideTourPromise = (): undefined => undefined

export type GuideTourProgrammaticScrollRunner = (element: HTMLElement) => void

export type RequestGuideTourProgrammaticScrollInput = {
  element: HTMLElement
  scrollTargetId: string | null
  activationKey: string | null
  scrollRequestId: number
  isScrollRequestStale: () => boolean
  scrolledActivationKey: string | null
  scrollAttempt: number
  maxScrollAttempts: number
  onScrollAttemptScheduled: (next: {
    scrolledActivationKey: string
    scrollAttempt: number
    pendingScrollReveal: boolean
  }) => void
  runProgrammaticScroll: GuideTourProgrammaticScrollRunner
}

export const requestGuideTourProgrammaticScroll = (
  input: RequestGuideTourProgrammaticScrollInput
): void => {
  const { scrollTargetId, activationKey } = input

  if (!scrollTargetId || !activationKey) {
    return
  }

  let scrolledActivationKey = input.scrolledActivationKey
  let scrollAttempt = input.scrollAttempt

  if (scrolledActivationKey === activationKey) {
    if (scrollAttempt >= input.maxScrollAttempts) {
      return
    }
  } else {
    scrolledActivationKey = activationKey
    scrollAttempt = 0
  }

  scrollAttempt += 1
  input.onScrollAttemptScheduled({
    scrolledActivationKey,
    scrollAttempt,
    pendingScrollReveal: true,
  })

  const runScrollRequest = () => {
    if (input.isScrollRequestStale()) {
      return
    }

    runAfterGuideTourLayoutFrames(() => {
      if (input.isScrollRequestStale()) {
        return
      }

      input.runProgrammaticScroll(input.element)
    })
  }

  waitForGuideTourPageScrollUnlock()
    .then(runScrollRequest)
    .catch(ignoreRejectedGuideTourPromise)
}

export type RevealGuideTourBoundsAfterScrollInput = {
  targetId: string
  abort: { aborted: boolean }
  isScrollRequestStale: () => boolean
  setBounds: (bounds: SpotlightBounds | null) => void
  onRevealComplete: (bounds: SpotlightBounds) => void
  requestProgrammaticScroll: (element: HTMLElement) => void
}

const finishGuideTourReveal = (
  input: RevealGuideTourBoundsAfterScrollInput,
  settledElement: HTMLElement
): void => {
  if (input.abort.aborted) {
    return
  }

  const settledBounds = getGuideTourTargetBounds(settledElement)
  const settledInsideModal = isGuideTourTargetInsideActiveModal(settledElement)
  const settledVisible =
    settledInsideModal ||
    isGuideTourTargetVisibleInViewport(settledBounds, {
      width: window.innerWidth,
      height: window.innerHeight,
    })

  if (!settledVisible) {
    input.requestProgrammaticScroll(settledElement)
    return
  }

  input.onRevealComplete(settledBounds)
}

export const revealGuideTourBoundsAfterScroll = async (
  input: RevealGuideTourBoundsAfterScrollInput
): Promise<void> => {
  await waitForGuideTourScrollSettle()

  if (input.abort.aborted || input.isScrollRequestStale()) {
    return
  }

  const settledElement = findGuideTourTargetElement(input.targetId)

  if (!settledElement) {
    input.setBounds(null)
    return
  }

  await waitForGuideTourTargetPositionStability(settledElement)

  if (input.abort.aborted || input.isScrollRequestStale()) {
    return
  }

  finishGuideTourReveal(input, settledElement)
}
